const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const itemNameOf = (item = {}) =>
  String(
    item?.item_name ??
      item?.product_name ??
      item?.productname ??
      item?.name ??
      "Item"
  ).trim() || "Item";

const itemIdOf = (item = {}) =>
  item?.product_id ?? item?.productid ?? item?.id ?? "";

export const buildCategorySalesGroups = (items = [], categoryNameOf) => {
  const categories = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const categoryName = String(
      categoryNameOf?.(item) ?? item?.category_name ?? item?.categoryName ?? "Uncategorized"
    ).trim() || "Uncategorized";
    const itemName = itemNameOf(item);
    const productId = itemIdOf(item);
    const itemKey = `${String(productId || "").trim().toLowerCase()}:${itemName.toLowerCase()}`;
    const quantity = numberValue(
      item?.total_quantity ?? item?.qty ?? item?.quantity ?? item?.qtyy
    );
    const amount = item?.total_amount !== undefined
      ? numberValue(item.total_amount)
      : item?.amount !== undefined
        ? numberValue(item.amount)
        : quantity * numberValue(
            item?.priceaftertax ?? item?.taxedprice ?? item?.unit_price ?? item?.price
          );
    const orders = numberValue(item?.total_orders ?? item?.orders);

    if (!categories.has(categoryName)) {
      categories.set(categoryName, {
        category_name: categoryName,
        itemsMap: new Map(),
        total_quantity: 0,
        total_orders: 0,
        total_amount: 0,
      });
    }

    const category = categories.get(categoryName);
    const existing = category.itemsMap.get(itemKey) || {
      product_id: productId || itemKey,
      item_name: itemName,
      total_quantity: 0,
      total_orders: 0,
      total_amount: 0,
    };
    existing.total_quantity += quantity;
    existing.total_orders += orders;
    existing.total_amount += amount;
    category.itemsMap.set(itemKey, existing);
    category.total_quantity += quantity;
    category.total_orders += orders;
    category.total_amount += amount;
  });

  return Array.from(categories.values(), (category) => ({
    category_name: category.category_name,
    item_count: category.itemsMap.size,
    total_quantity: category.total_quantity,
    total_orders: category.total_orders,
    total_amount: category.total_amount,
    items: Array.from(category.itemsMap.values()).sort((a, b) =>
      a.item_name.localeCompare(b.item_name)
    ),
  })).sort((a, b) => a.category_name.localeCompare(b.category_name));
};

const center = (text, width) => {
  const value = String(text ?? "").trim();
  if (value.length >= width) return value.slice(0, width);
  const left = Math.floor((width - value.length) / 2);
  return `${" ".repeat(left)}${value}`;
};

const padLeft = (text, width) =>
  String(text ?? "").slice(0, width).padStart(width, " ");

const wrap = (text, width) => {
  let remaining = String(text ?? "").trim();
  const lines = [];
  while (remaining) {
    if (remaining.length <= width) {
      lines.push(remaining);
      break;
    }
    const slice = remaining.slice(0, width + 1);
    const splitAt = slice.lastIndexOf(" ");
    const length = splitAt > 0 ? splitAt : width;
    lines.push(remaining.slice(0, length));
    remaining = remaining.slice(length).trimStart();
  }
  return lines.length ? lines : [""];
};

const formatNumber = (value) => {
  const number = numberValue(value);
  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

const formatPrintedAt = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hour24 = date.getHours();
  const hour12 = hour24 % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const meridiem = hour24 >= 12 ? "PM" : "AM";
  return `${day}-${month}-${year} ${hour12}:${minutes} ${meridiem}`;
};

export const buildCategorySalesReceiptText = ({
  restaurant = "Bill Sarthi",
  addressLines = [],
  registrationLines = [],
  periodLabel = "",
  printedAt = new Date(),
  categories = [],
  showCategoryHeadings = true,
  title = "Item Sales Register",
  sectionTotalLabel = "Category Total",
} = {}, lineWidth = 32) => {
  const width = Math.max(24, Number(lineWidth) || 32);
  const qtyWidth = width >= 48 ? 7 : 5;
  const amountWidth = width >= 48 ? 12 : 9;
  const nameWidth = width - qtyWidth - amountWidth - 2;
  const separator = "-".repeat(width);
  const row = (name, qty, amount) =>
    `${String(name ?? "").slice(0, nameWidth).padEnd(nameWidth, " ")} ${padLeft(qty, qtyWidth)} ${padLeft(amount, amountWidth)}`;
  const lines = [];
  const pushCentered = (text) =>
    wrap(text, width).forEach((part) => lines.push(center(part, width)));

  pushCentered(restaurant);
  [...addressLines, ...registrationLines]
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .forEach(pushCentered);
  lines.push("");
  pushCentered(title);
  if (periodLabel) pushCentered(periodLabel);
  const stamp = printedAt instanceof Date ? printedAt : new Date(printedAt);
  if (!Number.isNaN(stamp.getTime())) {
    pushCentered(`Printed at: ${formatPrintedAt(stamp)}`);
  }
  lines.push(separator);
  lines.push(row("Particulars", "QTY", "Amount"));
  lines.push(separator);

  categories.forEach((category) => {
    if (showCategoryHeadings) {
      pushCentered(category?.category_name || "Uncategorized");
      lines.push(separator);
    }
    (category?.items || []).forEach((item) => {
      const nameLines = wrap(item?.item_name || "Item", nameWidth);
      lines.push(row(nameLines[0], formatNumber(item?.total_quantity), formatNumber(item?.total_amount)));
      nameLines.slice(1).forEach((nameLine) => lines.push(row(nameLine, "", "")));
    });
    if (showCategoryHeadings) {
      lines.push(separator);
      lines.push(row(sectionTotalLabel, formatNumber(category?.total_quantity), formatNumber(category?.total_amount)));
      lines.push(separator);
    }
  });

  const grandQty = categories.reduce(
    (sum, category) => sum + numberValue(category?.total_quantity),
    0
  );
  const grandAmount = categories.reduce(
    (sum, category) => sum + numberValue(category?.total_amount),
    0
  );
  lines.push(row("Grand Total", formatNumber(grandQty), formatNumber(grandAmount)));
  lines.push("=".repeat(width));
  return lines.join("\n");
};

export const buildItemSalesReceiptText = ({ items = [], ...header } = {}, lineWidth = 32) => {
  const totalQuantity = items.reduce(
    (sum, item) => sum + numberValue(item?.total_quantity ?? item?.qty ?? item?.quantity),
    0
  );
  const totalAmount = items.reduce(
    (sum, item) => sum + numberValue(item?.total_amount ?? item?.amount),
    0
  );
  return buildCategorySalesReceiptText(
    {
      ...header,
      showCategoryHeadings: false,
      categories: [{
        category_name: "",
        items,
        total_quantity: totalQuantity,
        total_amount: totalAmount,
      }],
    },
    lineWidth
  );
};
